import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Flex } from 'ui/acro'
import CommentAuthor from './CommentAuthor'
import { ResponsiveSVG } from 'ui/acro'
import ThumbsUp from '../../../assets/images/thumbup.svg'
import ThumbsUpSelected from '../../../assets/images/thumbupselected.svg'
import { ReadMoreLegacy } from 'ui/acro'
import commentAxios from 'api/commentsAxios'
import ReportComment from './ReportComment'
import { useWindowResize } from 'hooks'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-width: none;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 24px;
  gap: 12px;
  @media (max-width: 768px) {
    flex-direction: column;
  }
  .toggle {
    font-size: 14px;
    color: #000;
    font-family: 'Proxima-Nova';
    font-weight: 600;
    line-height: 24px;
    letter-spacing: 0.28px;
    text-transform: uppercase;
    text-decoration: underline;
    text-decoration-thickness: 1px;
    cursor: pointer;
  }
`

const CommentText = styled.div`
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 150% */
  letter-spacing: 0.352px;
  width: 60%;
  @media (max-width: 768px) {
    width: 100%;
  }
`

const CommentActions = styled(Flex)`
  justify-content: space-between;
  align-items: center;
`
const Likes = styled(Flex)`
  gap: 4px;
  align-items: center;
`
const Options = styled(Flex)`
  gap: 12px;
`

const Option = styled.div`
  color: #636363;
  text-align: center;
  font-family: 'Proxima-Nova';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; /* 128.571% */
  letter-spacing: 0.308px;
  text-decoration-line: underline;
  text-decoration-style: solid;
  cursor: pointer;
`

const OptionReported = styled.div`
  color: #636363;
  text-align: center;
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.352px;
`
const Reply = styled(Flex)`
  font-family: 'Proxima-Nova';
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 18px; /* 128.571% */
  letter-spacing: 0.308px;
  text-decoration-line: underline;
  text-decoration-style: solid;
`

// Comment Component
const Comment = ({
  comment,
  onEdit,
  onDelete,
  setActiveReplyId,
  activeReplyId,
  type,
  id,
  userName,
  setComments,
  setShowReplies,
  setShowSnackbar,
  invokeGA4Event
}) => {
  const [userLiked, setUserLiked] = useState(comment?.likedByMe)
  const [likeCount, setLikeCount] = useState(comment?.likes)
  const [showReportModal, setShowReportModal] = useState(false)
  const [reportCmt, setReportComt] = useState(false)
  const [lineCount, setLineCount] = useState(0)
  const { width } = useWindowResize()
  const commentRef = useRef(null)
  const [load, setLoad] = useState(false)
  const isMobile = width <= 480

  useEffect(() => {
    setLikeCount(comment?.likes)
    setUserLiked(comment?.likedByMe)
  }, [comment?.likes])

  useEffect(() => {
    const div = commentRef.current
    if (div) {
      const computedStyle = window.getComputedStyle(div)
      const lineHeight = parseFloat(computedStyle.lineHeight)
      const height = div.offsetHeight
      const lines = Math.round(height / lineHeight)
      setLineCount(lines)
    }
  }, [])

  const getReadMoreInitialHeight = () => {
    if (!isMobile) {
      if (lineCount === 1) return 24
      if (lineCount === 2) return 48
      if (lineCount === 3) return 72
      if (lineCount >= 4) return 96
      return 96
    } else if (isMobile) {
      if (lineCount === 1) return 24
      if (lineCount === 2) return 48
      if (lineCount === 3) return 72
      if (lineCount === 4) return 96
      if (lineCount >= 5) return 120
      return 120
    }
  }

  const onLike = async () => {
    if (load) return
    let previous = {}
    setComments((prev) =>
      prev.map((state) => {
        if (state.commentId !== comment.commentId) {
          return state
        }
        previous = { ...state }
        const currentLikes = state.likes || 0
        const likes = state.likedByMe ? currentLikes - 1 : currentLikes + 1
        return { ...state, likes, likedByMe: !state.likedByMe }
      })
    )
    try {
      setLoad(true)
      const response = await commentAxios.post(
        '/content-interaction/reaction',
        {
          content_type: type,
          content_id: id,
          type: 'LIKE',
          comment_id: comment?.commentId
        }
      )
      if (response.status === 200) {
        invokeGA4Event('like')
        setComments((prev) =>
          prev.map((individualComment) =>
            individualComment.commentId === comment.commentId
              ? {
                  ...individualComment,
                  likedByMe: response?.data?.likedByMe,
                  likes: response?.data?.likes
                }
              : individualComment
          )
        )
      }
    } catch (err) {
      console.log(err)
      setComments((prev) =>
        prev.map((state) => {
          if (state.commentId !== comment.commentId) {
            return state
          }
          return previous
        })
      )
    } finally {
      setLoad(false)
    }
  }

  const handleCloseReportModal = () => setShowReportModal(false)
  const handleShowReportModal = () => setShowReportModal(true)

  const reported =
    comment.reported || reportCmt ? (
      <OptionReported>Reported</OptionReported>
    ) : (
      <Option onClick={handleShowReportModal}>Report</Option>
    )

  return (
    <Container>
      <CommentAuthor user={comment.user} comment={comment} />
      <ReadMoreLegacy
        initialHeight={getReadMoreInitialHeight()}
        noReadMoreHeightCompensator={0}
      >
        <CommentText ref={commentRef}>{comment.text}</CommentText>
      </ReadMoreLegacy>
      {showReportModal && (
        <ReportComment
          show={showReportModal}
          closeHandler={handleCloseReportModal}
          id={id}
          reply={''}
          commentId={comment.commentId}
          type={type}
          setShowSnackbar={setShowSnackbar}
          setReportComt={setReportComt}
        />
      )}
      <CommentActions>
        <Likes>
          <ResponsiveSVG
            SVG={userLiked ? ThumbsUpSelected : ThumbsUp}
            style={{
              cursor: 'pointer'
            }}
            width='20px'
            height='20px'
            onClick={() => onLike()}
          />
          {likeCount > 0 && <>{likeCount}</>}
        </Likes>
        {userName === comment?.user?.username ? (
          <Options>
            <Option onClick={() => onEdit(comment.commentId)}>Edit</Option>
            <Option onClick={() => onDelete(comment.commentId)}>Delete</Option>
          </Options>
        ) : (
          <>{reported}</>
        )}
      </CommentActions>
      {activeReplyId !== comment.commentId && (
        <Reply>
          <span
            style={{
              cursor: 'pointer'
            }}
            onClick={() => {
              setActiveReplyId(comment.commentId)
            }}
          >
            Reply
          </span>
        </Reply>
      )}
    </Container>
  )
}

export default Comment
